import React, { useEffect, useState, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Text } from '@react-three/drei';
import * as THREE from 'three';
import SponsortsBar from '../../structural/screen/sponsorsBar';
import SponsorBarMobile from '../../structural/screen/sponsorsBarMobile';

const blockTypes = [
  {
    id: 1,
    type: 'bloque',
    traba: 2,
    brand: "HOGARTI",
    name: "BLOQUE CONCRETO 15X20X40",
    width: 0.15,
    height: 0.20,
    length: 0.40,
    weight: 5.76,
    price: 0.70,
    color: "#9aa9a8",
    img: ""
  },
  {
    id: 2,
    type: 'traba',
    traba: null,
    brand: "HOGARTI",
    name: "BLOQUE CONCRETO 15X20X40",
    width: 0.15,
    height: 0.20,
    length: 0.20,
    weight: 5.76,
    price: 0.70,
    color: "#9aa9a8",
    img: ""
  },
  {
    id: 3,
    type: 'bloque',
    traba: null,
    brand: "HOGARTI",
    name: "BLOQUE CONCRETO 10X20X40",
    width: 0.10,
    height: 0.20,
    length: 0.40,
    weight: 5.76,
    price: 0.56,
    color: "#9aa9a8",
    img: ""
  },  
];

const mortarDensity = 1.6;
const mortarPrice = 120;
const plasterThicknessDefault = 0.015;
const plasterPrice = 15;
const paintPrice = 8;
const paintCoverage = 0.1;
const paintCoatsDefault = 2;

const ConcreteBlockWall = () => 
{  
  const [openMenu, setOpenMenu] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [hasTrabas, setHasTrabas] = useState(false);
  
  const menuRef = useRef();

  const [wallDimensions, setWallDimensions] = useState({
    length: 3.45,
    height: 2.30
  });

  const [mortarGap, setMortarGap] = useState(1);
  const [selectedBlock, setSelectedBlock] = useState(blockTypes[0]);
  
  const [plasterEnabled, setPlasterEnabled] = useState(false);
  const [plasterThickness, setPlasterThickness] = useState(plasterThicknessDefault);
  const [plasterBothSides, setPlasterBothSides] = useState(true);
  const [paintEnabled, setPaintEnabled] = useState(false);
  const [paintBothSides, setPaintBothSides] = useState(false);
  const [paintCoats, setPaintCoats] = useState(paintCoatsDefault);
  
  const [results, setResults] = useState({
    fullBlocks: 0,
    fullTrabas: 0,
    cutBlocks: [],
    mortar: 0,
    totalWeight: 0,
    weightPerMeter: 0,
    wallArea: 0,
    blocksCost: 0,
    trabasCost: 0,
    mortarCost: 0,
    plasterArea: 0,
    plasterVolume: 0,
    plasterCost: 0,
    paintArea: 0,
    paintLiters: 0,
    paintCost: 0,
    totalCost: 0
  });

  const getTrabaForBlock = (selected) => 
  {
    if (!selected.traba) 
    {
      let halfBlock = 
      {
        length: selected.length/2,
        weight: selected.weight/2,
        price: selected.price/2
      }
      setHasTrabas(false);
      return halfBlock;
    } 
    else 
    {
      setHasTrabas(true);
      return blockTypes.find(b => b.id === selected.traba);
    }
  };

  useEffect(() => {
    calculateMaterials();
  }, [wallDimensions, selectedBlock, mortarGap, plasterEnabled, plasterThickness, plasterBothSides, paintEnabled, paintBothSides, paintCoats]);

  const calculateMaterials = () => {
    const { length, height } = wallDimensions;
    const { length: blockL, height: blockH, width: thickness, weight, price } = selectedBlock;
    const traba = getTrabaForBlock(selectedBlock);
    const trabaL = traba ? traba.length : 0;
    const trabaPrice = traba ? traba.price : 0;

    const mortarGapMeters = mortarGap / 100;

    let fullBlocks = 0;
    let fullTrabas = 0;
    const cutBlocks = [];
    let currentY = 0;
    let rowCounter = 0;
    let totalMortarX = 0;
    let totalMortarY = 0;
    let totalRows = 0;

    const wallArea = length * height;

    while (currentY < height) {
      const rowHeight = Math.min(blockH, height - currentY);
      const isOddRow = rowCounter % 2 !== 0;
      let currentX = 0;
      let blocksInRow = 0;
      let lastBlockWasTraba = false;
      
      while (currentX < length) {
        const remainingX = length - currentX;
        let blockSizeX = blockL;
        let isTraba = false;
        
        if (isOddRow && traba && blocksInRow === 0 && remainingX >= trabaL && !lastBlockWasTraba) {
          blockSizeX = trabaL;
          isTraba = true;
          lastBlockWasTraba = true;
        } else if (remainingX < blockL) {
          if (traba && remainingX >= trabaL && !lastBlockWasTraba) {
            blockSizeX = trabaL;
            isTraba = true;
            lastBlockWasTraba = true;
          } else {
            blockSizeX = remainingX;
            lastBlockWasTraba = false;
          }
        } else {
          lastBlockWasTraba = false;
        }

        if (blockSizeX > 0.01) {
          if (Math.abs(blockSizeX - (isTraba ? trabaL : blockL)) < 0.001 && 
              Math.abs(rowHeight - blockH) < 0.001) {
            if (isTraba) {
              fullTrabas++;
            } else {
              fullBlocks++;
            }
          } else {
            cutBlocks.push({
              width: thickness,
              height: rowHeight,
              length: blockSizeX,
              isTraba
            });
          }
          blocksInRow++;
          currentX += blockSizeX;
          
          if (currentX < length) {
            totalMortarX += thickness * rowHeight * mortarGapMeters;
            currentX += mortarGapMeters;
          }
        } else {
          break;
        }
      }
      
      currentY += rowHeight;
      totalRows++;
      
      if (currentY < height) {
        totalMortarY += length * thickness * mortarGapMeters;
        currentY += mortarGapMeters;
      }
      
      rowCounter++;
    }

    const verticalMortar = totalMortarX + (thickness * height * mortarGapMeters * 2);
    const horizontalMortar = totalMortarY + (length * thickness * mortarGapMeters * 2);
    const mortar = verticalMortar + horizontalMortar;

    const cutBlocksWeight = cutBlocks.reduce((sum, block) => {
      const blockWeight = block.isTraba ? traba.weight : weight;
      const blockFullLength = block.isTraba ? trabaL : blockL;
      return sum + (block.length * blockWeight / blockFullLength);
    }, 0);

    const cutBlocksCost = cutBlocks.reduce((sum, block) => {
      const blockCost = block.isTraba ? trabaPrice : price;
      const blockFullLength = block.isTraba ? trabaL : blockL;
      return sum + (block.length * blockCost / blockFullLength);
    }, 0);

    const totalWeight = (fullBlocks * weight) + (fullTrabas * (traba ? traba.weight : 0)) + cutBlocksWeight + (mortar * mortarDensity * 1000);

    const blocksCost = fullBlocks * price;
    const trabasCost = fullTrabas * (traba ? trabaPrice : 0);
    const mortarCost = mortar * mortarPrice;
    const totalBlocksCost = blocksCost + trabasCost + cutBlocksCost;
    
    let plasterArea = 0;
    let plasterVolume = 0;
    let plasterCost = 0;
    
    if (plasterEnabled) {
      plasterArea = wallArea * (plasterBothSides ? 2 : 1);
      plasterVolume = plasterArea * plasterThickness;
      plasterCost = plasterArea * plasterPrice;
    }
    
    let paintArea = 0;
    let paintLiters = 0;
    let paintCost = 0;
    
    if (paintEnabled) {
      paintArea = wallArea * (paintBothSides ? 2 : 1);
      paintLiters = paintArea * paintCoverage * paintCoats;
      paintCost = paintArea * paintPrice * paintCoats;
    }
    
    const totalCost = totalBlocksCost + mortarCost + plasterCost + paintCost;

    setResults({
      fullBlocks,
      fullTrabas,
      cutBlocks,
      mortar,
      totalWeight,
      weightPerMeter: totalWeight / length,
      verticalMortar,
      horizontalMortar,
      wallArea,
      blocksCost,
      trabasCost,
      cutBlocksCost,
      mortarCost,
      plasterArea,
      plasterVolume,
      plasterCost,
      paintArea,
      paintLiters,
      paintCost,
      totalCost
    });
  };

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    const numericValue = parseFloat(value) || 0;
    const roundedValue = Math.max(0.05, Math.round(numericValue * 20) / 20);
    setWallDimensions(prev => ({
      ...prev,
      [name]: roundedValue
    }));
  };

  const handleMortarGapChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    const roundedValue = Math.round(value * 2) / 2;
    const clampedValue = Math.max(0.5, Math.min(5, roundedValue));
    setMortarGap(clampedValue);
  };

  const handlePlasterThicknessChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    const clampedValue = Math.max(0.005, Math.min(0.05, value));
    setPlasterThickness(clampedValue);
  };

  const handlePaintCoatsChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    const clampedValue = Math.max(1, Math.min(5, value));
    setPaintCoats(clampedValue);
  };

  const formatDimension = (value) => {
    const cmValue = Math.round(value * 100);
    return cmValue % 1 === 0 ? cmValue : cmValue.toFixed(1);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatVolume = (value) => {
    return value.toFixed(3).replace('.', ',');
  };

  const renderCutBlocks = () => {
    const counts = {};
    results.cutBlocks.forEach(block => {
      const type = block.isTraba ? 'Traba' : 'Bloque';
      const key = `${type} ${formatDimension(block.width)}x${formatDimension(block.height)}x${formatDimension(block.length)}`;
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.entries(counts).map(([size, count]) => (
      <p key={size} className="text-sm">
        <span className="font-semibold">{size} cm:</span> {count}
      </p>
    ));
  };

  const availableBlocks = blockTypes.filter(block => block.type === 'bloque');

  const toggleMenu = (menuName) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex flex-col h-screen">
      <div ref={menuRef} className="bg-gray-800 text-white flex justify-between items-center px-4 py-2 z-[3000]">
        <div className="bg-gray-800 text-white flex justify-between items-center px-4 py-2 z-[3000] h-[40px]">        
          <div className="relative">
            <button 
              className="hover:bg-gray-600 px-3 py-1 rounded bg-gray-700"
              onClick={() => toggleMenu('creditos')}
            >
              Créditos
            </button>
            <div className={`absolute left-0 mt-1 ${openMenu === 'creditos' ? 'block' : 'hidden'} bg-white text-black rounded shadow w-[300px]`}>
              <a 
                href="https://www.linkedin.com/in/entornoceo/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block font-bold text-black px-4 py-2 hover:bg-gray-100 w-full text-left cursor-pointer"
              >
                Creado por: Ing. Javier Hernández
              </a>             
            </div>
          </div>
        </div>
        <div>
          <span className="font-bold uppercase">Paredes de Bloque de Concreto</span>
        </div>
      </div>

      <div className="flex sm:flex-row flex-col h-full overflow-hidden">
        <div className="flex flex-col flex-1 z-50 bg-gray-50 sm:w-9/12 sm:h-full h-1/2">
          <div className="flex bg-gray-50 p-1 h-[60px] sm:hidden m-auto">
            <SponsorBarMobile/>
          </div>

          <div className="h-full w-full border-b-2 sm:border-0 border-black">
            <Canvas>
              <ambientLight intensity={0.5} />
              <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
              <PerspectiveCamera makeDefault position={[4, 1, -4]} />
              <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
              <Wall3D 
                wall={wallDimensions} 
                block={selectedBlock} 
                mortarGap={mortarGap / 100}
                plasterEnabled={plasterEnabled}
                plasterThickness={plasterThickness}
                plasterBothSides={plasterBothSides}
                paintEnabled={paintEnabled}
                paintBothSides={paintBothSides}
              />
            </Canvas>
          </div>

          <div className="sm:flex bg-gray-50 p-1 h-[100px] hidden m-auto">
            <SponsortsBar/>
          </div>
        </div>        

        <div className="flex flex-col p-4 bg-gray-200 overflow-y-auto z-50 sm:w-3/12 sm:min-w-[300px] sm:h-full h-1/2">          
          <h2 className="text-xl font-semibold mb-4">Parámetros de la Pared</h2>
              
          <div className="space-y-4">
            {['length', 'height'].map((dim) => (
              <div key={dim}>
                <label className="block text-sm font-medium text-gray-700">
                  {dim === 'length' ? `Longitud${ isMobile ? ` - ${wallDimensions[dim]}` : ''}` : `Altura${ isMobile ? ` - ${wallDimensions[dim]}` : ''}`} (m)
                </label>
                <input
                  type={ isMobile ? "range" : "number"}
                  inputMode="decimal"
                  pattern="[0-9]*"
                  name={dim}
                  value={wallDimensions[dim]}
                  onChange={handleDimensionChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                  min="1"
                  max={ dim === 'length' ? isMobile ? 10 : 20 : isMobile ? 5 : 10 }
                  step="0.05"
                />
                <p className="text-xs text-gray-500">{`Incrementos de 5 cm (mínimo 1 metro - máximo ${dim === 'length' ? isMobile ? 10 : 20 : isMobile ? 5 : 10}) metros`}</p>
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {`Espesor de mortero${ isMobile ? ` - ${mortarGap}` : ''} (cm)`}
              </label>
              <input
                type={ isMobile ? "range" : "number"}
                inputMode="decimal"
                pattern="[0-9]*"
                value={mortarGap}
                onChange={handleMortarGapChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                min="1"
                max="5"
                step="0.5"
              />
              <p className="text-xs text-gray-500">Incrementos de 0.5 cm (mínimo 0.5 cm, máximo 5 cm)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de bloque</label>
              <select
                value={selectedBlock.id}
                onChange={(e) => setSelectedBlock(blockTypes.find(b => b.id === parseInt(e.target.value)))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
              >
                {availableBlocks.map(block => (
                  <option key={block.id} value={block.id}>{block.brand} - {block.name}</option>
                ))}
              </select>
            </div>

            <div className="border-t border-gray-400 pt-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="plasterEnabled"
                  checked={plasterEnabled}
                  onChange={(e) => setPlasterEnabled(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="plasterEnabled" className="text-sm font-medium text-gray-700">Aplicar enlucido</label>
              </div>

              {plasterEnabled && (
                <div className="ml-6 mt-2 space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Espesor del enlucido (m)
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={plasterThickness}
                      onChange={handlePlasterThicknessChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                      min="0.005"
                      max="0.05"
                      step="0.001"
                    />
                    <p className="text-xs text-gray-500">Entre 0.5 cm (0.005 m) y 5 cm (0.05 m)</p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="plasterBothSides"
                      checked={plasterBothSides}
                      onChange={(e) => setPlasterBothSides(e.target.checked)}
                      className="mr-2"
                    />
                    <label htmlFor="plasterBothSides" className="text-sm font-medium text-gray-700">Enlucir ambas caras</label>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-400 pt-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="paintEnabled"
                  checked={paintEnabled}
                  onChange={(e) => setPaintEnabled(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="paintEnabled" className="text-sm font-medium text-gray-700">Aplicar pintura</label>
              </div>

              {paintEnabled && (
                <div className="ml-6 mt-2 space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Número de manos
                    </label>
                    <input
                      type="number"
                      value={paintCoats}
                      onChange={handlePaintCoatsChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                      min="1"
                      max="5"
                    />
                    <p className="text-xs text-gray-500">Entre 1 y 5 manos</p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="paintBothSides"
                      checked={paintBothSides}
                      onChange={(e) => setPaintBothSides(e.target.checked)}
                      className="mr-2"
                    />
                    <label htmlFor="paintBothSides" className="text-sm font-medium text-gray-700">Pintar ambas caras</label>
                  </div>
                </div>
              )}
            </div>
          </div>

          <h3 className="text-lg font-medium my-3">Resultados</h3>

          <div className="space-y-2 mb-4">
            <p><span className="font-semibold">Área de la pared:</span> {results.wallArea.toFixed(2)} m²</p>
            
            <h4 className="font-semibold mt-2 uppercase underline">Materiales:</h4>
            <p><span className="font-semibold">Bloques completos:</span> {results.fullBlocks}</p>
            
            {results.fullTrabas > 0 && hasTrabas && (
              <p><span className="font-semibold">Trabas completas:</span> {results.fullTrabas}</p>
            )}
            
            <h4 className="font-semibold mt-2"> {hasTrabas ? "Bloques/Trabas cortados:" : "Bloques cortados:"} </h4>
            {results.fullTrabas > 0 && !hasTrabas && (
              <p><span className="font-semibold">Medios Bloques:</span> {results.fullTrabas}</p>
            )}
            
            {results.cutBlocks.length > 0 ? renderCutBlocks() : <p className="text-sm">No hay bloques/trabas cortados</p>}
            
            <h4 className="font-semibold mt-2 uppercase underline">Mortero:</h4>
            <p className="text-sm"><span className="font-semibold">Volumen total:</span> {formatVolume(results.mortar)} m³</p>
            <p className="text-sm"><span className="font-semibold">Mortero vertical:</span> {formatVolume(results.verticalMortar || 0)} m³</p>
            <p className="text-sm"><span className="font-semibold">Mortero horizontal:</span> {formatVolume(results.horizontalMortar || 0)} m³</p>
            
            {plasterEnabled && (
              <>
                <h4 className="font-semibold mt-2 uppercase underline">Enlucido:</h4>
                <p className="text-sm"><span className="font-semibold">Área a enlucir:</span> {results.plasterArea.toFixed(2)} m²</p>
                <p className="text-sm"><span className="font-semibold">Volumen de enlucido:</span> {formatVolume(results.plasterVolume)} m³</p>
              </>
            )}
            
            {paintEnabled && (
              <>
                <h4 className="font-semibold mt-2 uppercase underline">Pintura:</h4>
                <p className="text-sm"><span className="font-semibold">Área a pintar:</span> {results.paintArea.toFixed(2)} m²</p>
                <p className="text-sm"><span className="font-semibold">Litros de pintura:</span> {results.paintLiters.toFixed(2)} L</p>
              </>
            )}
            
            <h4 className="font-semibold mt-2 uppercase underline">Pesos:</h4>
            <p className="text-sm"><span className="font-semibold">Peso total:</span> {results.totalWeight.toFixed(0)} kg</p>
            <p className="text-sm"><span className="font-semibold">Peso por metro lineal:</span> {results.weightPerMeter.toFixed(0)} kg/m</p>
            
            <h4 className="font-semibold mt-2 uppercase underline">Costos:</h4>
            <p className="text-sm"><span className="font-semibold">Costo bloques:</span> {formatCurrency(results.blocksCost)}</p>
            {results.fullTrabas > 0 && (
              <p className="text-sm"><span className="font-semibold">Costo trabas:</span> {formatCurrency(results.trabasCost)}</p>
            )}
            {results.cutBlocks.length > 0 && (
              <p className="text-sm"><span className="font-semibold">Costo bloques cortados:</span> {formatCurrency(results.cutBlocksCost)}</p>
            )}
            <p className="text-sm"><span className="font-semibold">Costo mortero:</span> {formatCurrency(results.mortarCost)}</p>
            {plasterEnabled && (
              <p className="text-sm"><span className="font-semibold">Costo enlucido:</span> {formatCurrency(results.plasterCost)}</p>
            )}
            {paintEnabled && (
              <p className="text-sm"><span className="font-semibold">Costo pintura:</span> {formatCurrency(results.paintCost)}</p>
            )}
            <p className="text-sm font-bold"><span className="font-semibold">Costo total:</span> {formatCurrency(results.totalCost)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Wall3D = ({ 
  wall, 
  block, 
  mortarGap,
  plasterEnabled,
  plasterThickness,
  plasterBothSides,
  paintEnabled,
  paintBothSides }) => 
{
  const { length, height } = wall;
  const { length: blockL, height: blockH, width: thickness, color, traba } = block;
  
  let trabaData;
  if(traba)
  {
    trabaData = blockTypes.find(b => b.id === traba);
  }
  else
  {
    trabaData = 
    {
      length: blockL/2, // meters
      color: color
    }
  }

  const lengthTextRef = useRef();
  const heightTextRef = useRef();

  useFrame(({ camera }) => 
  {
    if (lengthTextRef.current) lengthTextRef.current.lookAt(camera.position);
    if (heightTextRef.current) heightTextRef.current.lookAt(camera.position);
  });

  const blocks = [];
  const plasters = [];
  const paints = [];

  let currentY = -height/2;
  let rowCounter = 0;

  // Mortero inferior
  blocks.push(
    <mesh
      key="mortar-bottom"
      position={[0, -height/2 - mortarGap/2, 0]}
    >
      <boxGeometry args={[length + mortarGap*2, mortarGap, thickness]} />
      <meshStandardMaterial color="#cccccc" />
    </mesh>
  );

  // Mortero izquierdo
  blocks.push(
    <mesh
      key="mortar-left"
      position={[-length/2 - mortarGap/2, 0, 0]}
    >
      <boxGeometry args={[mortarGap, height + mortarGap*2, thickness]} />
      <meshStandardMaterial color="#cccccc" />
    </mesh>
  );

  // Mortero derecho
  blocks.push(
    <mesh
      key="mortar-right"
      position={[length/2 + mortarGap/2, 0, 0]}
    >
      <boxGeometry args={[mortarGap, height + mortarGap*2, thickness]} />
      <meshStandardMaterial color="#cccccc" />
    </mesh>
  );  

  // Mortero superior
  blocks.push(
    <mesh
      key="mortar-top"
      position={[0, height/2 + mortarGap/2, 0]}
    >
      <boxGeometry args={[length + mortarGap*2, mortarGap, thickness]} />
      <meshStandardMaterial color="#cccccc" />
    </mesh>
  );

  // Bloques
  while (currentY < height/2) 
  {
    const rowHeight = Math.min(blockH, height/2 - currentY);
    if (rowHeight <= 0) break;

    let currentX = -length/2;
    const isOddRow = rowCounter % 2 !== 0;
    let blocksInRow = 0;
    let lastBlockWasTraba = false;
    
    while (currentX < length/2) {
      const remainingX = length/2 - currentX;
      let blockSizeX = blockL;
      let blockColor = color;
      let isTraba = false;
      
      // Lógica para colocar traba solo al inicio de filas impares
      if (isOddRow && trabaData && blocksInRow === 0 && remainingX >= trabaData.length) {
        blockSizeX = trabaData.length;
        blockColor = trabaData.color;
        isTraba = true;
      } 
      // Manejo especial para el FINAL de la fila (lado derecho)
      else if (remainingX < blockL) {
        // NUNCA colocar traba al final si va a quedar sola
        // Siempre preferir un bloque cortado al final
        blockSizeX = remainingX;
        
        // Solo en casos muy específicos donde el corte coincide exactamente con tamaño de traba
        if (trabaData && Math.abs(remainingX - trabaData.length) < 0.001) {
          blockSizeX = trabaData.length;
          blockColor = trabaData.color;
          isTraba = true;
        }
      }
    
      // Ajuste final para no sobrepasar el borde
      if (currentX + blockSizeX > length/2) {
        blockSizeX = length/2 - currentX;
      }
    
      if (blockSizeX > 0.01) {
        blocks.push(
          <group key={`${currentY}-${currentX}`}>
            <mesh
              position={[currentX + blockSizeX/2, currentY + rowHeight/2, 0]}
            >
              <boxGeometry args={[blockSizeX, rowHeight, thickness]} />
              <meshStandardMaterial color={blockColor} />
            </mesh>
            <lineSegments position={[currentX + blockSizeX/2, currentY + rowHeight/2, 0]}>
              <edgesGeometry args={[new THREE.BoxGeometry(blockSizeX, rowHeight, thickness)]} />
              <lineBasicMaterial color="#111111" linewidth={2} />
            </lineSegments>
          </group>
        );
    
        currentX += blockSizeX;
        blocksInRow++;
        
        // Añadir mortero excepto después del último bloque
        if (currentX < length/2) {
          blocks.push(
            <mesh
              key={`mortar-${currentY}-${currentX}`}
              position={[currentX + mortarGap/2, currentY + rowHeight/2, 0]}
            >
              <boxGeometry args={[mortarGap, rowHeight, thickness]} />
              <meshStandardMaterial color="#cccccc" />
            </mesh>
          );
          currentX += mortarGap;
        }
      } else {
        break;
      }
    }
    
    currentY += rowHeight;
    
    // Añadir mortero horizontal excepto después de la última fila
    if (currentY < height/2)
    {
      // Visualización del mortero horizontal
      blocks.push(
        <mesh
          key={`mortar-h-${currentY}`}
          position={[0, currentY + mortarGap/2, 0]}
        >
          <boxGeometry args={[length, mortarGap, thickness]} />
          <meshStandardMaterial color="#cccccc" />
        </mesh>
      );
      currentY += mortarGap;
    }
    
    rowCounter++;
  }

  // Enlucido frontal (capa completa)
  if (plasterEnabled) 
  {
    console.log(height)
    plasters.push(
      <mesh
        key="plaster-front"
        position={[0, 0, thickness/2 + plasterThickness/2]}
      >
        <boxGeometry args={[length, height, plasterThickness]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
    );
  }

  return (
    <group>
      <group ref={lengthTextRef} position={[0, -height/2 - mortarGap - 0.15, 0]}>
        <Text color="black" anchorX="center" anchorY="middle" fontSize={0.1}>
          {length.toFixed(2)} m
        </Text>
      </group>
      
      <group ref={heightTextRef} position={[-length/2 - mortarGap - 0.15, 0, 0]}>
        <Text 
          color="black" 
          anchorX="center" 
          anchorY="middle" 
          fontSize={0.1}
          rotation={[0, 0, Math.PI/2]}
        >
          {height.toFixed(2)} m
        </Text>
      </group>
      
      {blocks}
      {plasters}
    </group>
  );
};

export default ConcreteBlockWall;